'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { SlideForm, type SlideFormRef } from '@/components/partials/slides'
import { type CreateSlideParams } from '@/types/slide'
import { useCreateSlide } from '@/hooks'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const slideFormRef = useRef<SlideFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createSlide, isPending: isLoading } = useCreateSlide()

  const handleSubmit = (data: CreateSlideParams) => {
    createSlide(data, {
      onSuccess: async newSlide => {
        setIsRedirecting(true)
        queryClient.setQueryData(['slide', newSlide.id], newSlide)
        await queryClient.invalidateQueries({ queryKey: ['slides'] })
        toast.success(t('created_successfully'))
        router.push(`/galleries/slides/${newSlide.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateSlideParams) => {
    createSlide(data, {
      onSuccess: async newSlide => {
        queryClient.setQueryData(['slide', newSlide.id], newSlide)
        await queryClient.invalidateQueries({ queryKey: ['slides'] })
        const detailUrl = `/galleries/slides/${newSlide.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        slideFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (slideFormRef.current) {
        slideFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/galleries/slides')
  }

  return (
    <SlideForm
      key={formKey}
      ref={slideFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
